import React from 'react';
import { checkDomain, EntriCloseEventDetail, EntriConfig, EntriSuccessEventDetail, showEntri } from 'entrijs';
import { isEqual, isNil } from 'lodash-es';

import { useAppDispatch, useAppSelector } from '@/store';
import {
  sessionDomainConnectConfig,
  sessionDomainConnectSetupBegin,
  sessionDomainConnectSetupFailure,
  sessionDomainConnectSetupSuccess,
  setDomainCheck,
} from '@/store/slices/session';
import { useTranslation } from 'react-i18next';

export function useDomainConnect() {
  const dispatch = useAppDispatch();
  const { i18n } = useTranslation();
  const session = useAppSelector((state) => state.session, isEqual);

  const [isEntriOpen, setIsEntriOpen] = React.useState<boolean>(false);

  const handleSuccess = React.useCallback(
    (event: Event) => {
      const { detail } = event as CustomEvent<EntriSuccessEventDetail>;

      const eventData = JSON.stringify(detail);

      dispatch(
        sessionDomainConnectSetupSuccess({
          event_type: 'success',
          event_data: eventData,
        }),
      );
    },
    [dispatch],
  );

  const handleClose = React.useCallback(
    (event: Event) => {
      const { detail } = event as CustomEvent<EntriCloseEventDetail>;

      const eventData = JSON.stringify(detail);

      if (detail.success) {
        dispatch(
          sessionDomainConnectSetupSuccess({
            event_type: 'success-close-modal',
            event_data: eventData,
          }),
        );
      } else if (detail.error) {
        dispatch(
          sessionDomainConnectSetupFailure({
            event_type: 'failure-close-modal',
            event_data: eventData,
          }),
        );
      } else {
        dispatch(
          sessionDomainConnectSetupFailure({
            event_type: 'cancel-close-modal',
            event_data: eventData,
          }),
        );
      }

      setIsEntriOpen(false);
    },
    [dispatch],
  );

  // Disabled, kept for reference
  // const handleStepChange = React.useCallback(
  //   (event: Event) => {
  //     const { detail } = event as CustomEvent<EntriStepChangeEventDetail>;
  //
  //     const eventData = JSON.stringify(detail);
  //
  //     dispatch(
  //       sessionDomainConnectSetupFailure({
  //         event_type: 'modal-step-change',
  //         event_data: eventData,
  //       }),
  //     );
  //   },
  //   [dispatch],
  // );

  // Disabled, kept for reference
  // const handleManualClick = React.useCallback(
  //   (event: Event) => {
  //     const { detail } = event as CustomEvent<EntriManualSetupDocumentationClickEventDetail>;
  //
  //     const eventData = JSON.stringify(detail);
  //
  //     dispatch(
  //       sessionDomainConnectSetupFailure({
  //         event_type: 'modal-manual-click',
  //         event_data: eventData,
  //       }),
  //     );
  //   },
  //   [dispatch],
  // );

  React.useEffect(() => {
    // Triggered when the user successfully completes domain setup.
    window.addEventListener('onSuccess', handleSuccess);
    // Triggered when the modal is closed, whether successful or not.
    window.addEventListener('onEntriClose', handleClose);
    // Disabled, kept for reference
    // Triggered when the user moves between screens.
    // window.addEventListener('onEntriStepChange', handleStepChange);
    // Disabled, kept for reference.
    // Triggered when the user clicks the manual setup guide link.
    // window.addEventListener('onEntriManualSetupDocumentationClick', handleManualClick);

    return () => {
      window.removeEventListener('onSuccess', handleSuccess);
      window.removeEventListener('onEntriClose', handleClose);
      // Disabled, kept for reference
      // window.removeEventListener('onEntriStepChange', handleStepChange);
      // Disabled, kept for reference
      // window.removeEventListener('onEntriManualSetupDocumentationClick', handleManualClick);
    };
  }, [handleClose, handleSuccess]);

  const handleConnect = React.useCallback(() => {
    dispatch(
      sessionDomainConnectConfig({
        event_type: 'config',
        event_data: '',
      }),
    );
  }, [dispatch]);

  const handleOpenConnect = React.useCallback(() => {
    if (
      session.domainConnect.loaded &&
      !isNil(session.domainConnect.config) &&
      !isNil(session.data.customization_data?.domain)
    ) {
      // Debugging only, for now
      checkDomain(session.data.customization_data.domain, session.domainConnect.config).then((response) => {
        setDomainCheck(response);
      });

      // Adjust config from back-end with some front-end settings
      const fullEntriConfig: EntriConfig = {
        ...session.domainConnect.config,
        prefilledDomain: session.data.customization_data.domain,
        locale: i18n.language,
      };

      // Open Entri modal
      setIsEntriOpen(true);

      showEntri(fullEntriConfig).then(() => {
        dispatch(
          sessionDomainConnectSetupBegin({
            event_type: 'initialize',
            event_data: JSON.stringify(fullEntriConfig),
          }),
        );
      });
    }
  }, [
    dispatch,
    i18n.language,
    session.data.customization_data?.domain,
    session.domainConnect.config,
    session.domainConnect.loaded,
  ]);

  return React.useMemo(
    () => ({
      onConnect: handleConnect,
      onOpenConnect: handleOpenConnect,
      isConnectOpen: isEntriOpen,
    }),
    [handleConnect, handleOpenConnect, isEntriOpen],
  );
}
